import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { scrollShadow } from '../../styles/mixins';
import { hideScrollbar } from '../../styles/scrollbar';
import { TABLE_ROW_BG_COLOR_VAR, TABLE_Z_INDEX } from './HBTable.styles';
import { useDetectScroll } from '../../hooks/useDetectScroll';
import { ScrollSyncPane } from 'react-scroll-sync';
import { HBTableHorizontalShadowSyncScrollContainerProps } from './HBTable.types';
import { vars } from '../../theme';

function HBTableHorizontalShadowSyncScrollContainer({
    children,
    table
}: HBTableHorizontalShadowSyncScrollContainerProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    const { isScrolled, isScrolledToEnd } = useDetectScroll({
        elementRef: ref,
        direction: 'horizontal'
    });

    const { isFirstColumnPinned, isLastColumnPinned, isLastColumnFloating } =
        table.options.meta;

    return (
        <ShadowContainer
            $isScrolled={isScrolled}
            $isScrolledToEnd={isScrolledToEnd}
            $isFirstColumnPinned={isFirstColumnPinned}
            $isLastColumnPinned={isLastColumnPinned}
            $isLastColumnFloating={isLastColumnFloating}
        >
            <ScrollSyncPane innerRef={ref}>
                <ScrollContainer ref={ref}>{children}</ScrollContainer>
            </ScrollSyncPane>
        </ShadowContainer>
    );
}

export default HBTableHorizontalShadowSyncScrollContainer;

// -------------------------- Styles -------------------------- //

const ScrollContainer = styled.div`
    overflow: auto;
    ${hideScrollbar}
`;

interface PinProps {
    $isScrolled: boolean;
    $isScrolledToEnd: boolean;
    $isFirstColumnPinned: boolean;
    $isLastColumnPinned: boolean;
    $isLastColumnFloating: boolean;
}

const commonShadowProps: Pick<
    Parameters<typeof scrollShadow>[0],
    'size' | 'offsetStart'
> = {
    size: 12,
    offsetStart: -1
};

const ShadowContainer = styled.div<PinProps>`
    position: relative;

    ${({ $isFirstColumnPinned, $isScrolled }) =>
        $isFirstColumnPinned
            ? css`
                  th:first-child,
                  td:first-child {
                      z-index: ${TABLE_Z_INDEX.BOTTOM};
                      position: sticky;
                      left: 0;
                      ${scrollShadow({
                          isShadowActive: $isScrolled,
                          direction: 'right',
                          ...commonShadowProps
                      })}
                  }
                  td:first-child {
                      background-color: inherit;
                  }
              `
            : css`
                  ${scrollShadow({
                      isShadowActive: $isScrolled,
                      direction: 'left',
                      isInset: true,
                      ...commonShadowProps
                  })}
              `}

    ${({ $isLastColumnPinned, $isLastColumnFloating, $isScrolledToEnd }) =>
        $isLastColumnPinned
            ? css`
                  th:last-child,
                  td:last-child {
                      position: sticky;
                      right: 0;
                  }
                  td:last-child {
                      background-color: inherit;
                  }
                  ${$isLastColumnFloating
                      ? css`
                            th:last-child {
                                ${applyShadowToFloatingElement(
                                    !$isScrolledToEnd,
                                    vars.colors.surface.itemInset
                                )}
                            }

                            td:last-child {
                                opacity: 0;
                                transition: opacity 60ms;
                            }
                            tr:is(
                                    :hover,
                                    :focus-visible,
                                    :focus-within,
                                    :has([data-state='open'])
                                )
                                td:last-child {
                                opacity: 1;
                                ${applyShadowToFloatingElement(
                                    !$isScrolledToEnd,
                                    `var(${TABLE_ROW_BG_COLOR_VAR})` // Because it's not possible to use the 'inherit' keyword in a linear-gradient
                                )}
                            }

                            ${applyShadowToTableRightEdge}
                        `
                      : css`
                            th:last-child,
                            td:last-child {
                                ${scrollShadow({
                                    isShadowActive: !$isScrolledToEnd,
                                    direction: 'left',
                                    ...commonShadowProps
                                })}
                            }
                        `}
              `
            : applyShadowToTableRightEdge}
`;

function applyShadowToFloatingElement(isShadowActive: boolean, color: string) {
    return scrollShadow({
        isShadowActive,
        direction: 'left',
        size: 40,
        isFlat: true,
        flatColor: color
    });
}

function applyShadowToTableRightEdge({ $isScrolledToEnd }: PinProps) {
    return scrollShadow({
        isShadowActive: !$isScrolledToEnd,
        direction: 'right',
        isInset: true,
        ...commonShadowProps
    });
}
